import React, { useState, useEffect } from 'react';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import Login from './components/Login';
import Chat from './components/Chat';
import { disconnectSocket, getSocket } from './socket';
import { setActiveRoomId } from './utils/activeChat';
import './App.css';
import { initializePushNotificationsForUser, unregisterPushTokenForCurrentUser, checkNotificationPermissionStatus, attachBottomPermissionBarIfNeeded } from './messaging';
import { App as CapacitorApp } from '@capacitor/app';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setLoading(false);

      if (currentUser) {
        // Initialize push notifications (web only if permission is already granted)
        try {
          if (typeof Notification === 'undefined' || Notification.permission === 'granted') {
            initializePushNotificationsForUser(currentUser);
          }
        } catch (_) {}
      } else {
        disconnectSocket();
        unregisterPushTokenForCurrentUser();
        setActiveRoomId(null); // Clear active room on logout to prevent privacy leak
      }
    });

    // Check notification permission status on app load
    checkNotificationPermissionStatus();
    // Show bottom sticky bar if not granted; clicking requests and initializes if granted
    attachBottomPermissionBarIfNeeded(async () => {
      if (auth.currentUser) {
        try {
          if (typeof Notification === 'undefined' || Notification.permission === 'granted') {
            await initializePushNotificationsForUser(auth.currentUser);
          }
        } catch (e) {
          console.warn('Init after permission grant failed:', e);
        }
      }
    });

    return () => {
      unsubscribe();
      disconnectSocket();
    };
  }, []);

  // Handle app state changes for Android lifecycle
  useEffect(() => {
    let isHandlingStateChange = false;
    const isNative =
      typeof window !== 'undefined' &&
      window.Capacitor &&
      typeof window.Capacitor.getPlatform === 'function' &&
      window.Capacitor.getPlatform() !== 'web';

    const handleAppStateChange = async (state) => {
      // Web should keep the socket alive; native apps disconnect in background.
      if (!isNative) return;
      if (isHandlingStateChange) return;
      isHandlingStateChange = true;
      
      console.log('📱 App state changed:', state);
      
      if (state.isActive) {
        // Let chat UI decide whether to reconnect (connect-on-chat mode).
        console.log('📱 App resumed');
      } else {
        // App went to background - disconnect socket immediately for push notifications
        console.log('📱 App backgrounded, disconnecting socket for push notifications...');
        disconnectSocket();
      }
      
      // Reset flag after a short delay
      setTimeout(() => {
        isHandlingStateChange = false;
      }, 1000);
    };

    // Visibility fallback for when Capacitor event is delayed/missed
    const handleVisibilityChange = async () => {
      if (document.hidden) {
        // Only disconnect on native, where JS gets suspended and push notifications are used.
        if (isNative) {
          console.log('📱 Document hidden (native), disconnecting socket...');
          disconnectSocket();
        }
        return;
      } else {
        // Let chat UI decide whether to reconnect (connect-on-chat mode).
        console.log('📱 Document visible');
      }
    };

    // Also handle browser beforeunload for web version
    const handleBeforeUnload = () => {
      console.log('📱 App closing, disconnecting socket...');
      disconnectSocket();
    };

    // Handle Android back button
    const handleBackButton = async (event) => {
      console.log('📱 Android back button pressed', event);

      // Check if we can go back in the app
      try {
        const canGoBack = await CapacitorApp.canGoBack();
        console.log('📱 Can go back:', canGoBack);
        
        if (!canGoBack) {
          console.log('📱 Cannot go back, preventing default behavior');
          // Prevent the app from closing
          if (event && event.preventDefault) {
            event.preventDefault();
          }
          return false;
        }
      } catch (error) {
        console.error('❌ Error checking canGoBack:', error);
      }

      // Emit custom event that Chat component can listen to
      const backEvent = new CustomEvent('androidBackButton', {
        detail: { timestamp: Date.now(), originalEvent: event }
      });
      window.dispatchEvent(backEvent);

      // ALWAYS prevent default behavior to stop app from closing
      if (event && event.preventDefault) {
        event.preventDefault();
      }
      
      // Also try to stop propagation
      if (event && event.stopPropagation) {
        event.stopPropagation();
      }
      
      // Return false to prevent default behavior
      return false;
    };

    // Listen for app state changes
    const setupListeners = async () => {
      try {
        if (!isNative) return;
        await CapacitorApp.addListener('appStateChange', handleAppStateChange);

        // Register back button listener with explicit configuration
        const backButtonListener = await CapacitorApp.addListener('backButton', handleBackButton);
        console.log('📱 Back button listener registered:', backButtonListener);

        console.log('📱 Capacitor listeners registered successfully');
      } catch (error) {
        console.error('❌ Failed to register Capacitor listeners:', error);
        
        // Fallback: try to register back button with different approach
        try {
          console.log('📱 Attempting fallback back button registration...');
          CapacitorApp.addListener('backButton', handleBackButton);
        } catch (fallbackError) {
          console.error('❌ Fallback back button registration also failed:', fallbackError);
        }
      }
    };

    // Also try to register the back button listener immediately
    try {
      if (isNative) {
        console.log('📱 Registering back button listener immediately...');
        CapacitorApp.addListener('backButton', handleBackButton);
      }
    } catch (error) {
      console.error('❌ Immediate back button registration failed:', error);
    }

    setupListeners();

    // Add global back button handler for Android
    if (isNative) {
      window.handleAndroidBackButton = () => {
        console.log('📱 Global Android back button handler called');
        handleBackButton({});
      };
    }

    // Fallback hardware back button detection for Android
    const handleHardwareBackButton = (event) => {
      // Check if this is likely an Android hardware back button press
      if (event.key === 'Backspace' && event.metaKey) {
        console.log('📱 Hardware back button detected');
        handleBackButton(event);
      }
    };

    window.addEventListener('keydown', handleHardwareBackButton);
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (isNative) {
        CapacitorApp.removeAllListeners();
      }
      window.removeEventListener('keydown', handleHardwareBackButton);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (isNative) {
        delete window.handleAndroidBackButton;
      }
    };
  }, [user]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="App">
      {user ? <Chat user={user} /> : <Login />}
    </div>
  );
}

export default App;

